import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  GraduationCap, Play, Loader2, CheckCircle2, BookOpen, Monitor,
  Edit2, Image as ImageIcon, Zap, X, Save, Upload
} from "lucide-react";

export default function ContractUniversity() {
  const [lessons, setLessons] = useState([]);
  const [tracks, setTracks] = useState({});
  const [progress, setProgress] = useState({ watched: 0, total: 0 });
  const [isAdmin, setIsAdmin] = useState(false);
  const [active, setActive] = useState(null);
  const [genId, setGenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/university/lessons");
      setLessons(r.data.lessons || []);
      setTracks(r.data.tracks || {});
      setProgress(r.data.progress || { watched: 0, total: 0 });
      setIsAdmin(!!r.data.is_admin);
    } catch (e) { toast.error("Failed to load lessons"); }
  };
  useEffect(() => { load(); }, []);

  // Poll while any lesson is generating in the background
  useEffect(() => {
    const anyGen = lessons.some(l => l.generating);
    if (anyGen && !pollRef.current) {
      pollRef.current = setInterval(load, 5000);
    } else if (!anyGen && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [lessons]);

  const play = async (l) => {
    if (!l.generated) {
      setGenId(l.id);
      try {
        toast.info("Generating video with AI voice — takes ~30s");
        await api.post(`/university/lessons/${l.id}/generate`, {}, { timeout: 240000 });
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Generation failed");
        setGenId(null);
        return;
      }
      setGenId(null);
    }
    setActive({ ...l, generated: true });
  };

  const generateAll = async (force = false) => {
    if (!confirm(force ? "Regenerate ALL 16 videos? Existing MP4s will be deleted first." : "Queue every un-generated lesson for background generation?")) return;
    setBatchBusy(true);
    try {
      const r = await api.post(`/university/generate-all?force=${force}`, {});
      toast.success(`${r.data.started} of ${r.data.total} queued — page will refresh as each completes`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBatchBusy(false); }
  };

  const markWatched = async (lessonId) => {
    try { await api.post(`/university/lessons/${lessonId}/watch`); load(); } catch { }
  };

  const byTrack = { A: [], B: [] };
  lessons.forEach(l => { byTrack[l.track]?.push(l); });

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="contract-university">
      <div className="flex items-start gap-3 mb-6">
        <GraduationCap className="w-8 h-8 text-[#005289]" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Contract University</h1>
          <p className="text-sm text-slate-500">AI-narrated video lessons — each under 2 minutes.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={() => generateAll(false)} disabled={batchBusy}
                    className="h-9 px-4 bg-[#005289] text-white text-sm font-semibold rounded flex items-center gap-2 disabled:opacity-50"
                    data-testid="batch-generate-btn">
              <Zap className="w-4 h-4" /> {batchBusy ? "Queueing…" : "Generate all"}
            </button>
            <button onClick={() => generateAll(true)} disabled={batchBusy}
                    className="h-9 px-3 border border-slate-300 text-sm rounded" data-testid="batch-regen-btn">
              Force regen
            </button>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" data-testid="uni-progress">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Your progress</div>
          <div className="text-xs text-slate-500">
            <b>{progress.watched}</b> of <b>{progress.total}</b> lessons completed
          </div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress.total ? (progress.watched / progress.total) * 100 : 0}%` }} />
        </div>
      </div>

      {["A", "B"].map(t => (
        <div key={t} className="mb-10" data-testid={`track-${t}`}>
          <div className="flex items-center gap-2 mb-4">
            {t === "A" ? <BookOpen className="w-5 h-5 text-emerald-700" /> : <Monitor className="w-5 h-5 text-blue-700" />}
            <h2 className="text-lg font-bold">
              Track {t}: {tracks[t]}
            </h2>
            <span className="text-xs text-slate-500">({byTrack[t].length} lessons)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {byTrack[t].map(l => (
              <div key={l.id} className="relative bg-white border border-slate-200 rounded-md p-4 hover:border-[#005289] hover:shadow-md transition group"
                   data-testid={`lesson-card-${l.id}`}>
                <button onClick={() => play(l)} disabled={genId === l.id || l.generating}
                        className="text-left w-full disabled:opacity-60">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${t === "A" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                      {(genId === l.id || l.generating) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                    </div>
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Lesson {l.id.toUpperCase()}</div>
                    {l.watched && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 ml-auto" title="Watched" data-testid={`lesson-watched-${l.id}`} />}
                    {!l.watched && l.generated && <CheckCircle2 className="w-3.5 h-3.5 text-slate-300 ml-auto" title="Video ready" />}
                  </div>
                  <div className="font-semibold text-slate-900 text-sm mb-1">{l.title}</div>
                  <div className="text-xs text-slate-500">{l.description}</div>
                  <div className="text-[10px] text-slate-400 mt-2 flex items-center gap-2">
                    <span>{l.slide_count} slides</span>
                    <span>·</span>
                    <span>{l.generated ? `~${Math.round(l.duration_seconds)}s` : l.generating ? "Generating…" : "Not generated"}</span>
                    {l.has_screenshots && <ImageIcon className="w-3 h-3 text-blue-500" title="Uses real screenshots" />}
                  </div>
                </button>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingId(l.id); }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-slate-100 hover:bg-slate-200 opacity-0 group-hover:opacity-100 transition"
                    title="Edit lesson"
                    data-testid={`lesson-edit-${l.id}`}
                  ><Edit2 className="w-3 h-3" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="video-player-modal">
          <div className="bg-white rounded-lg max-w-4xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">Lesson {active.id.toUpperCase()} · Track {active.track}</div>
                <div className="font-bold text-lg">{active.title}</div>
              </div>
              <button onClick={() => setActive(null)} className="text-xl px-3 hover:bg-slate-100 rounded" data-testid="player-close">×</button>
            </div>
            <video
              controls
              autoPlay
              className="w-full bg-black"
              src={`${backendBase}/api/university/lessons/${active.id}/video`}
              onEnded={() => markWatched(active.id)}
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (v.duration && v.currentTime / v.duration >= 0.9 && !active._marked) {
                  active._marked = true;
                  markWatched(active.id);
                }
              }}
              data-testid="lesson-video"
            />
            <div className="p-4 text-sm text-slate-600">{active.description}</div>
          </div>
        </div>
      )}

      {editingId && (
        <LessonEditor lessonId={editingId} onClose={() => setEditingId(null)} onSaved={() => { setEditingId(null); load(); }} />
      )}
    </div>
  );
}

// ---------- Admin lesson editor modal ----------
function LessonEditor({ lessonId, onClose, onSaved }) {
  const [lesson, setLesson] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regenAfter, setRegenAfter] = useState(true);

  useEffect(() => {
    api.get(`/university/lessons/${lessonId}`).then(r => setLesson(r.data)).catch(() => toast.error("Load failed"));
  }, [lessonId]);

  const save = async () => {
    if (!lesson) return;
    setBusy(true);
    try {
      const slides = lesson.slides.map(s => ({ title: s.title, bullets: s.bullets }));
      await api.patch(`/university/lessons/${lessonId}`, {
        title: lesson.title, description: lesson.description,
        script: lesson.script, slides,
      });
      toast.success("Saved");
      if (regenAfter) {
        toast.info("Regenerating video…");
        try {
          await api.post(`/university/lessons/${lessonId}/generate?force=true`, {}, { timeout: 240000 });
          toast.success("Video regenerated");
        } catch (e) { toast.error("Regen failed — try Generate manually"); }
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const uploadShot = async (index, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/university/lessons/${lessonId}/slides/${index}/screenshot`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Screenshot uploaded for slide ${index}`);
      // Reload to reflect has_screenshot flag
      const r = await api.get(`/university/lessons/${lessonId}`);
      setLesson(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const removeShot = async (index) => {
    try {
      await api.delete(`/university/lessons/${lessonId}/slides/${index}/screenshot`);
      const r = await api.get(`/university/lessons/${lessonId}`);
      setLesson(r.data);
    } catch { toast.error("Failed"); }
  };

  if (!lesson) return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-white rounded p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="lesson-editor">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-400">Edit lesson {lessonId.toUpperCase()}</div>
            <input className="text-lg font-bold border-b border-transparent hover:border-slate-200 focus:border-[#005289] focus:outline-none"
                   value={lesson.title} onChange={e => setLesson({ ...lesson, title: e.target.value })} data-testid="editor-title" />
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-5">
          <div>
            <label className="text-xs font-bold block mb-1">Description</label>
            <input className="w-full h-9 px-3 text-sm border border-slate-200 rounded" value={lesson.description} onChange={e => setLesson({ ...lesson, description: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1">Narration script (spoken by AI voice)</label>
            <textarea rows={6} className="w-full text-sm border border-slate-200 rounded p-3" value={lesson.script} onChange={e => setLesson({ ...lesson, script: e.target.value })} data-testid="editor-script" />
            <div className="text-[10px] text-slate-400 mt-1">Aim for 250-300 words for a ~2 min video.</div>
          </div>
          <div>
            <label className="text-xs font-bold block mb-2">Slides</label>
            {lesson.slides.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded p-3 mb-2" data-testid={`editor-slide-${i + 1}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Slide {i + 1}</div>
                  {s.has_screenshot && (
                    <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-800 rounded flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" /> Screenshot attached
                      <button className="ml-1 text-red-600 hover:underline" onClick={() => removeShot(i + 1)} data-testid={`slide-shot-remove-${i + 1}`}>remove</button>
                    </span>
                  )}
                  <label className="ml-auto text-[10px] cursor-pointer text-[#005289] hover:underline flex items-center gap-1">
                    <Upload className="w-3 h-3" /> {s.has_screenshot ? "Replace" : "Upload"} screenshot
                    <input type="file" accept="image/*" className="hidden" onChange={e => uploadShot(i + 1, e.target.files[0])} data-testid={`slide-shot-upload-${i + 1}`} />
                  </label>
                </div>
                <input className="w-full h-8 px-2 text-sm border border-slate-200 rounded mb-2" value={s.title} onChange={e => { const n = { ...lesson }; n.slides[i].title = e.target.value; setLesson(n); }} />
                <textarea rows={3} className="w-full text-xs border border-slate-200 rounded p-2" value={(s.bullets || []).join("\n")} onChange={e => { const n = { ...lesson }; n.slides[i].bullets = e.target.value.split("\n").filter(Boolean); setLesson(n); }} data-testid={`slide-bullets-${i + 1}`} />
                <div className="text-[10px] text-slate-400 mt-1">One bullet per line.</div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={regenAfter} onChange={e => setRegenAfter(e.target.checked)} data-testid="editor-regen-check" />
            Regenerate video after saving
          </label>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 border border-slate-300 rounded">Cancel</button>
            <button onClick={save} disabled={busy} className="text-sm px-4 py-2 bg-[#005289] text-white rounded disabled:opacity-50 flex items-center gap-2" data-testid="editor-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
